import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { getOne } from '~main/actions/clients';
import { selectCurrentClient, selectLoading } from '~main/selectors/clients/clients';

interface ClientIdentity {
  clientName: string;
  isLoading: boolean;
}

/**
 * Resolves the client identity for the care-plan sidebar. Fetches the client
 * directly (getOne) so deep links work without the full client list having been
 * loaded. The fetch is non-fatal: while pending — or if it fails — the name falls
 * back to a placeholder and the rest of the view still renders.
 *
 * Classification and program type are intentionally absent: no current client
 * payload provides them. The identity block gains those rows once the backend
 * exposes the fields.
 */
export const useClientIdentity = (clientId?: number): ClientIdentity => {
  const dispatch = useAppDispatch();
  const client = useAppSelector(selectCurrentClient);
  const loading = useAppSelector(selectLoading);

  useEffect(() => {
    if (clientId !== undefined) dispatch(getOne({ id: clientId }));
  }, [dispatch, clientId]);

  // main.clients.row is shared state — guard against a stale client from another page.
  const isCurrent = client?.id === clientId;

  let clientName = '';
  if (isCurrent && client) clientName = client.full_name;
  else if (clientId !== undefined) clientName = `Client #${clientId}`;

  return {
    clientName,
    isLoading: !isCurrent && loading === 'pending',
  };
};
